import { useParams, Link, Navigate } from "react-router-dom";
import DOMPurify from "dompurify";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { getPost, BLOG_POSTS } from "../data/blog";
import { ArrowRight, ArrowLeft } from "lucide-react";

// Default service link set used when a blog post doesn't specify its own.
// Used for SEO internal linking from articles to service pages.
const RELATED_SERVICES_DEFAULT = [
  { href: "/video-production-in-liverpool", label: "Video Production Liverpool", sub: "Filming, editing & motion graphics with free stills." },
  { href: "/photography-liverpool", label: "Photography Liverpool", sub: "Product, portrait, event & property shoots, UK wide." },
  { href: "/animation-liverpool", label: "Animation Liverpool", sub: "2D motion graphics, 3D visualisations & explainers." },
  { href: "/drone-filming-aerial-photography-liverpool", label: "Drone Filming Liverpool", sub: "CAA-licensed aerial filming & photography." },
  { href: "/white-label", label: "White Label for Agencies", sub: "Invisible video production under your brand." },
  { href: "/video-production-prices", label: "Pricing & Quote", sub: "Fixed-price quote within 4 working hours." },
];

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    image: post.cover,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: {
      "@type": "Organization",
      name: "Yellow Video Production",
      url: "https://www.yellowvideoproduction.com",
    },
    publisher: { "@id": "https://www.yellowvideoproduction.com/#organization" },
    mainEntityOfPage: `https://www.yellowvideoproduction.com/blog/${post.slug}`,
    keywords: post.keywords,
  };

  return (
    <main className="pt-28 pb-12" data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={post.metaTitle}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        keywords={post.keywords}
        ogImage={post.cover}
        jsonLd={articleJsonLd}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Video Blog", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
      />

      <article className="yv-container max-w-3xl">
        <Reveal>
          <Link to="/blog" className="text-white/50 hover:text-[var(--yellow)] text-sm inline-flex items-center gap-2 mb-8">
            <ArrowLeft size={14} /> Back to articles
          </Link>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-4">
            {post.readTime} · {new Date(post.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </p>
          <h1 className="yv-h1 max-w-4xl text-balance">{post.title}</h1>
          <p className="text-white/70 mt-6 text-lg leading-relaxed">{post.excerpt}</p>
        </Reveal>

        <Reveal delay={100} className="mt-10">
          <div className="aspect-[16/9] rounded-md overflow-hidden border border-white/10">
            <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </Reveal>

        <div className="mt-12 space-y-12">
          {post.sections.map((s, i) => (
            <Reveal key={i} delay={50}>
              <section>
                <h2 className="yv-h2 text-white mb-5">{s.h2}</h2>
                {s.body.split("\n\n").map((para, idx) => (
                  <p
                    key={idx}
                    className="text-white/80 leading-[1.85] mb-5 text-[17px]"
                    // Simple markdown: **bold**, • bullets, single newlines as <br/>. Sanitised with DOMPurify.
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        para
                          .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white">$1</strong>')
                          .replace(/\n•\s*/g, '<br/><span class="text-[var(--yellow)] mr-2 font-bold">•</span>')
                          .replace(/^•\s*/, '<span class="text-[var(--yellow)] mr-2 font-bold">•</span>')
                          .replace(/\n/g, '<br/>'),
                        { ALLOWED_TAGS: ['strong', 'br', 'span'], ALLOWED_ATTR: ['class'] }
                      ),
                    }}
                  />
                ))}
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal delay={100} className="mt-16">
          <div className="bg-[var(--yellow)]/10 border border-[var(--yellow)]/30 rounded-md p-8 text-center">
            <p className="yv-h3 text-white mb-4">{post.cta.text}</p>
            <Link to={post.cta.href} className="yv-btn yv-btn-primary inline-flex">
              Get a quote in 4 hours <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>

        {/* Related services - SEO internal linking */}
        <section className="mt-20" data-testid="related-services">
          <p className="yv-eyebrow mb-6">Yellow Services</p>
          <h2 className="yv-h3 text-white mb-6">Explore what we do</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(post.relatedServices || RELATED_SERVICES_DEFAULT).map((s) => (
              <Link
                key={s.href}
                to={s.href}
                className="group flex items-start gap-3 p-4 bg-white/[0.02] border border-white/10 rounded-md hover:border-[var(--yellow)] hover:bg-white/[0.04] transition"
                data-testid={`related-service-${s.href.replace(/[^a-z]/gi, '')}`}
              >
                <div>
                  <p className="text-white font-bold text-sm group-hover:text-[var(--yellow)] transition">{s.label}</p>
                  <p className="text-white/55 text-xs mt-1 leading-snug">{s.sub}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Related blog posts */}
        <section className="mt-16">
          <p className="yv-eyebrow mb-6">More from the Video Blog</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2).map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group block bg-white/[0.02] border border-white/10 rounded-md overflow-hidden hover:border-[var(--yellow)] transition">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                </div>
                <div className="p-5">
                  <h3 className="text-white font-bold group-hover:text-[var(--yellow)] transition leading-tight">{p.title}</h3>
                  <p className="text-white/60 text-xs mt-2">{p.readTime}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </article>
    </main>
  );
}
